import {
  Backdrop,
  CircularProgress,
  Container,
  Grid,
  Stack,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import {
  MgpButton,
  MgpTextboxFormik,
  MgpDD,
  MgpCheckbox,
} from "../../../components/global";
import "react-phone-input-2/lib/style.css";
import { isValidNumber, parsePhoneNumber } from "libphonenumber-js";
import { ENV } from "../../../helpers";
import { useMaster } from "../../../hooks";
import { useUser } from "../../../hooks/useUsers";
import Loader from "../../Loader/Loader";
import { colors } from "../../../theme/colors";
import FormControlLabel from "@mui/material/FormControlLabel";
import { CheckBox } from "@mui/icons-material";
import { useEffect, useMemo, useState, useRef } from "react";
import PhoneInput from "react-phone-input-2";

export const UpdateUser = ({
  setAlertOption,
  setMainPageloading,
  setMessage,
  setAlertType,
  closed = () => {},
  edit = {},
  self = false,
}) => {
  let {
    user_id = "",
    username = "",
    first_name = "",
    last_name = "",
    email = "",
    phone = "",
    role_id = "",
    hospital_mapping = "",
    is_active = true,
    address = "",
    city_id = "",
    gender = "",
    country_id = "",
    region_id = "",
    countryCode = "",
    primary_investigator_doctor_ids = [],
    is_sub = false,
    city = "",
  } = edit;

  const qualityUser = ENV.quality_user_role_id;
  const formikRef = useRef(null);

  const getCityIdPayload = (roleId, cityIdValue) => {
    if (Number(roleId) === Number(qualityUser)) {
      return null;
    }

    const cityValues = Array.isArray(cityIdValue)
      ? cityIdValue
      : cityIdValue === "" || cityIdValue == null
      ? []
      : [cityIdValue];
    const normalizedCityIds = cityValues
      .map((id) => Number(id))
      .filter((id) => !Number.isNaN(id) && id > 0);

    return normalizedCityIds[0] ?? null;
  };

  const {
    roles,
    cities,
    hospitals,
    countries,
    setSelectedCountry,
    genders,
    setSelectedRegion,
    regions,
    setSelectedCity,
    doctors_and_medical_leads,
    setSelectedHospital,
  } = useMaster();

  const { useAddUser, useUpdateUser } = useUser();
  const [AddUser] = useAddUser;
  const [UpdateUser] = useUpdateUser;
  const initialHospitalIds = hospital_mapping
    ? hospital_mapping.map((r) => String(r.hospital_id))
    : [];

  const cityids = useMemo(() => {
    return city ? [...new Set(city.map((r) => String(r.city_id)))] : [];
  }, [city]);
  const regionids = useMemo(() => {
    return city ? [...new Set(city.map((r) => String(r.region_id)))] : [];
  }, [city]);
  if (!country_id) {
    country_id = city
      ? [...new Set(city.map((r) => String(r.country_id)))]?.[0]
      : "";
  }

  const hospitalId =
    initialHospitalIds.length > 0 ? initialHospitalIds[0] : "" || "";

  const isQualityRole = Number(role_id) === Number(qualityUser);
  const initialRegionId = isQualityRole
    ? regionids
    : regionids[0] ?? (region_id ? String(region_id) : "");
  const initialCityId = isQualityRole
    ? cityids
    : cityids[0] ?? (city_id ? String(city_id) : "");

  const lastPrefillKeyRef = useRef("");
  useEffect(() => {
    if (!user_id) return;

    const prefillKey = JSON.stringify({
      user_id,
      role_id,
      country_id,
      initialRegionId,
      initialCityId,
      hospitalId,
    });
    if (lastPrefillKeyRef.current === prefillKey) return;
    lastPrefillKeyRef.current = prefillKey;

    // Ensure dependent dropdown options are loaded for edit mode.
    // These functions trigger the required API calls (regions -> cities -> hospitals -> doctors).
    if (country_id) setSelectedCountry(country_id);

    if (
      (Array.isArray(initialRegionId) && initialRegionId.length > 0) ||
      (!Array.isArray(initialRegionId) && initialRegionId)
    ) {
      setSelectedRegion(initialRegionId);
    }

    if (
      (Array.isArray(initialCityId) && initialCityId.length > 0) ||
      (!Array.isArray(initialCityId) && initialCityId)
    ) {
      setSelectedCity(initialCityId);
    }

    if (hospitalId) setSelectedHospital(hospitalId);
  }, [
    user_id,
    role_id,
    country_id,
    hospitalId,
    initialRegionId,
    initialCityId,
    setSelectedCountry,
    setSelectedRegion,
    setSelectedCity,
    setSelectedHospital,
  ]);

  const normalizePhoneCountryCode = (value) => {
    const code = String(value ?? "").trim().toLowerCase();
    return /^[a-z]{2}$/.test(code) ? code : "";
  };

  const getPhoneCountryCodeFromNumber = (phoneValue) => {
    const phone = String(phoneValue ?? "").trim();
    if (!phone) return "";

    try {
      const parsedNumber = parsePhoneNumber(
        phone.startsWith("+") ? phone : `+${phone}`
      );
      return normalizePhoneCountryCode(parsedNumber?.country);
    } catch (error) {
      return "";
    }
  };

  const isValidPhoneNumber = (phoneValue, countryCodeValue) => {
    const phone = String(phoneValue ?? "").trim().replace(/^\+/, "");
    if (!phone) return false;

    const normalizedCountryCode =
      normalizePhoneCountryCode(countryCodeValue) ||
      getPhoneCountryCodeFromNumber(phone);

    try {
      const parsedNumber = normalizedCountryCode
        ? parsePhoneNumber(`+${phone}`, normalizedCountryCode.toUpperCase())
        : parsePhoneNumber(`+${phone}`);
      return parsedNumber?.isValid() ?? false;
    } catch (error) {
      try {
        return isValidNumber(`+${phone}`);
      } catch (parseError) {
        return false;
      }
    }
  };

  const initialCountryCode =
    normalizePhoneCountryCode(countryCode) || getPhoneCountryCodeFromNumber(phone);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const validationSchema = yup.object().shape({
    username: yup.string().required("Required"),
    firstName: yup.string().required("Required"),
    lastName: yup.string().required("Required"),
    email: yup.string().required("Required"),
    // address: yup.string().required("Required"),
    regionId:
      parseInt(formikRef?.current?.values?.["roleId"]) ===
      parseInt(ENV.quality_user_role_id)
        ? yup.array().min(1, "Select at least one selection")
        : yup.string().required("Required"),
    cityId:
      parseInt(formikRef?.current?.values?.["roleId"]) ===
      parseInt(ENV.quality_user_role_id)
        ? yup.array().min(1, "Select at least one selection")
        : yup.string().required("Required"),
    countryId: yup.string().required("Required"),
    roleId: yup.string().required("Required"),
    phone: yup
      .string()
      .test("is-valid-phone", "Invalid phone number", function (value) {
        return isValidPhoneNumber(value, this.parent.countryCode);
      })
      .required("Required"),
    gender: yup.string().required("Required"),
    hospitalIds: yup.array().when("roleId", (roleId) => {
      if (parseInt(roleId) === parseInt(ENV.quality_user_role_id)) {
        return yup.array().min(1, "Select at least one hospital");
      }
      return yup.array().optional();
    }),
    hospitalId: yup.string().when("roleId", (roleId) => {
      if (
        parseInt(roleId) === parseInt(ENV.doctor_role_id) ||
        parseInt(roleId) === parseInt(ENV.medical_lead_role_id) ||
        parseInt(roleId) === parseInt(ENV.secondary_investigator_role_id)
      ) {
        return yup.string().required("Required");
      }
      return yup.string().nullable();
    }),
    primaryInvestigatorDoctorIds: yup.array().when(
      ["roleId", "isSub"],
      ([roleId, isSub]) => {
        if (
          parseInt(roleId) === parseInt(ENV.secondary_investigator_role_id) ||
          isSub
        ) {
          return yup
            .array()
            .min(1, "Select at least one primary investigator");
        }
        return yup.array().optional();
      }
    ),
  });

  console.log("william4", primary_investigator_doctor_ids);

  return (
    <>
      <Container fluid>
        <Formik
          innerRef={formikRef}
          initialValues={{
            username,
            firstName: first_name,
            lastName: last_name,
            email,
            phone: phone,
            countryCode: initialCountryCode,
            isActive: is_active,
            roleId: role_id,
            hospitalIds: initialHospitalIds,
            hospitalId:
              initialHospitalIds.length > 0 ? initialHospitalIds[0] : "" || "",
            address,
            cityId: initialCityId,
            countryId: country_id,
            gender,
            regionId: initialRegionId,
            primaryInvestigatorDoctorIds: primary_investigator_doctor_ids,
            isSub: is_sub,
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            setMainPageloading(true);
            setLoadingSubmit(true);
            const payload = {
              userId: values.userId,
              username: values.username,
              firstName: values.firstName,
              lastName: values.lastName,
              email: values.email.toLowerCase().trim(),
              phone: values.phone,
              address: values.address,
              roleId: Number(values.roleId),
              cityId: getCityIdPayload(values.roleId, values.cityId),
              countryId: Number(values.countryId),
              isActive: values.isActive,
              gender: Number(values.gender),
              hospitalId: (() => {
                if (Number(values.roleId) === Number(qualityUser)) {
                  return [...new Set(values.hospitalIds.map(Number))];
                } else if (
                  values.hospitalId === "" ||
                  values.hospitalId.length === 0
                ) {
                  return [];
                } else {
                  return [Number(values.hospitalId)];
                }
              })(),
              primaryInvestigatorDoctorIds: (() => {
                return [
                  ...new Set(values.primaryInvestigatorDoctorIds.map(Number)),
                ];
              })(),
              isSub: values.isSub,
            };

            if (user_id === "") {
              AddUser({
                variables: payload,
                onCompleted: () => {
                  setMainPageloading(false);
                  closed();
                  setMainPageloading(false);
                  setLoadingSubmit(false);
                  setAlertOption(true);
                  setMessage("User added successfully");
                  setAlertType("success");
                },
                onError: (err) => {
                  setMainPageloading(false);
                  setAlertOption(true);
                  setAlertType("error");
                  setMessage(err.message);
                },
              });
            } else {
              payload.userId = Number(user_id);
              UpdateUser({
                variables: payload,
                onCompleted: () => {
                  setMainPageloading(false);
                  closed();
                  setMainPageloading(false);
                  setAlertOption(true);
                  setAlertType("success");
                  setMessage("Profile has been updated");
                },
                onError: (err) => {
                  setMainPageloading(false);
                  setLoadingSubmit(false);
                  setAlertOption(true);
                  setAlertType("error");
                  setMessage(
                    err?.message ||
                      "Can't able to update user,Please try again later!"
                  );
                },
              });
            }
          }}
        >
          {({
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            touched,
            errors,
            values,
          }) => (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item md={6}>
                  <MgpTextboxFormik
                    fullWidth
                    size="small"
                    label="User Name"
                    name="username"
                    variant="outlined"
                    splits={"username".split(".")}
                    values={values}
                    onChange={handleChange}
                    setFieldValue={setFieldValue}
                    disabled={user_id === "" ? false : true}
                    onBlur={handleBlur}
                    error={Boolean(touched.username && errors.username)}
                    helperText={touched.username && errors.username}
                  />
                </Grid>

                <Grid item md={6}>
                  <MgpTextboxFormik
                    fullWidth
                    size="small"
                    label="First Name"
                    name="firstName"
                    variant="outlined"
                    splits={"firstName".split(".")}
                    values={values}
                    setFieldValue={setFieldValue}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(touched.firstName && errors.firstName)}
                    helperText={touched.firstName && errors.firstName}
                  />
                </Grid>

                <Grid item md={6}>
                  <MgpTextboxFormik
                    fullWidth
                    size="small"
                    label="Last Name"
                    name="lastName"
                    variant="outlined"
                    values={values}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    error={Boolean(touched.lastName && errors.lastName)}
                    helperText={touched.lastName && errors.lastName}
                  />
                </Grid>

                <Grid item md={6}>
                  <PhoneInput
                    style={{ width: "100%" }}
                    inputProps={{
                      style: {
                        borderColor:
                          touched.phone && errors.phone
                            ? colors.ERROR
                            : colors.GOLD,
                        width: "100%",
                        height: "41px",
                      },
                    }}
                    id="outlined-required"
                    label="Phone"
                    type="text"
                    name="phone"
                    fullWidth
                    value={values?.phone}
                    country={
                      normalizePhoneCountryCode(values?.countryCode) ||
                      getPhoneCountryCodeFromNumber(values?.phone) ||
                      undefined
                    }
                    onChange={(value, country) => {
                      setFieldValue(
                        "countryCode",
                        normalizePhoneCountryCode(country?.countryCode)
                      );
                      setFieldValue("phone", value);
                    }}
                    error={Boolean(touched.phone && errors.phone)}
                  />
                </Grid>

                <Grid item md={6}>
                  <MgpTextboxFormik
                    fullWidth
                    size="small"
                    label="Email"
                    name="email"
                    variant="outlined"
                    values={values}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    error={Boolean(touched.email && errors.email)}
                    helperText={touched.email && errors.email}
                  />
                </Grid>

                {/* Drop-downs for roles and hospital */}
                <Grid item md={6}>
                  <MgpDD
                    label="Role"
                    variant="outlined"
                    size="small"
                    name="roleId"
                    value={values.roleId}
                    onChange={(e) => {
                      // if(parseInt(e.target.value) !== parseInt(ENV.doctor_role_id))
                      if (parseInt(e.target.value) === parseInt(qualityUser)) {
                        setFieldValue("regionId", []);
                        setFieldValue("cityId", []);
                      } else {
                        setFieldValue("regionId", "");
                        setFieldValue("cityId", "");
                      }
                      setFieldValue("isSub", false);
                      setFieldValue("primaryInvestigatorDoctorIds", []);
                      handleChange(e);
                    }}
                    onBlur={handleBlur}
                    options={roles}
                    fullWidth
                    error={Boolean(touched.roleId && errors.roleId)}
                    helperText={touched.roleId && errors.roleId}
                    disabled={self ? true : false}
                  />
                </Grid>
                <Grid item md={6}>
                  <MgpDD
                    label="Country"
                    variant="outlined"
                    size="small"
                    name="countryId"
                    multiselect={false}
                    fullWidth
                    value={values.countryId}
                    options={countries}
                    onChange={(e) => {
                      handleChange(e);
                      if (parseInt(values?.roleId) === parseInt(qualityUser)) {
                        setFieldValue("regionId", []);
                        setFieldValue("cityId", []);
                      } else {
                        setFieldValue("regionId", "");
                        setFieldValue("cityId", "");
                      }

                      setFieldValue("hospitalId", "");
                      setFieldValue("hospitalIds", []);
                      setSelectedCountry(e.target.value);
                    }}
                    onBlur={handleBlur}
                    error={Boolean(touched.countryId && errors.countryId)}
                    helperText={touched.countryId && errors.countryId}
                    disabled={self ? true : false}
                  />
                </Grid>
                <Grid item md={6}>
                  <MgpDD
                    label="Region"
                    variant="outlined"
                    size="small"
                    name="regionId"
                    error={Boolean(touched["regionId"] && errors["regionId"])}
                    fullWidth
                    disabled={values["countryId"] == "" || self ? true : false}
                    onChange={(e) => {
                      handleChange(e);
                      if (parseInt(values?.roleId) === parseInt(qualityUser)) {
                        setFieldValue("cityId", []);
                      } else {
                        setFieldValue("cityId", "");
                      }
                      setFieldValue("hospitalId", "");
                      setFieldValue("hospitalIds", []);
                      setSelectedRegion(e.target.value);
                    }}
                    options={regions}
                    helperText={touched["regionId"] && errors["regionId"]}
                    onBlur={handleBlur}
                    value={values["regionId"]}
                    multiselect={
                      Number(values.roleId) === Number(qualityUser)
                        ? true
                        : false
                    }
                  />
                </Grid>
                <Grid item md={6}>
                  <MgpDD
                    label="City"
                    variant="outlined"
                    size="small"
                    name="cityId"
                    multiselect={
                      Number(values.roleId) === Number(qualityUser)
                        ? true
                        : false
                    }
                    fullWidth
                    value={values.cityId}
                    options={cities}
                    onChange={(e) => {
                      console.log("e.target.value", e.target.value);
                      handleChange(e);
                      setFieldValue("hospitalId", "");
                      setFieldValue("hospitalIds", []);
                      setSelectedCity(e.target.value);
                    }}
                    onBlur={handleBlur}
                    error={Boolean(touched.cityId && errors.cityId)}
                    helperText={touched.cityId && errors.cityId}
                    disabled={values["regionId"] == "" || self ? true : false}
                  />
                </Grid>

                {values.roleId == qualityUser ? (
                  <Grid item md={6}>
                    <MgpDD
                      label="Hospital(s)"
                      variant="outlined"
                      size="small"
                      name="hospitalIds"
                      multiselect={true}
                      fullWidth
                      value={values.hospitalIds}
                      options={hospitals}
                      onChange={(e) => {
                        // handleChange(e)
                        setFieldValue("hospitalIds", e.target.value);
                      }}
                      onBlur={handleBlur}
                      error={Boolean(touched.hospitalIds && errors.hospitalIds)}
                      helperText={touched.hospitalIds && errors.hospitalIds}
                      disabled={values["cityId"] == "" || self ? true : false}
                    />
                  </Grid>
                ) : values.roleId == ENV.doctor_role_id ||
                  values.roleId == ENV.medical_lead_role_id ||
                  values.roleId == ENV.secondary_investigator_role_id ? (
                  <Grid item md={6}>
                    <MgpDD
                      label="Hospital"
                      variant="outlined"
                      size="small"
                      name="hospitalId"
                      multiselect={false}
                      fullWidth
                      value={values.hospitalId}
                      options={hospitals}
                      onChange={(e) => {
                        // handleChange(e)

                        setFieldValue("hospitalId", e.target.value);
                        setFieldValue("primaryInvestigatorDoctorIds", []);
                        setSelectedHospital(e.target.value);
                      }}
                      onBlur={handleBlur}
                      error={Boolean(touched.hospitalId && errors.hospitalId)}
                      helperText={touched.hospitalId && errors.hospitalId}
                      disabled={values["cityId"] == "" || self ? true : false}
                    />
                  </Grid>
                ) : (
                  <Grid item md={6}>
                    <MgpDD
                      label={values.roleId ? "All Hospital(s)" : "Hospital"}
                      variant="outlined"
                      size="small"
                      name="hospitalId"
                      multiselect={false}
                      fullWidth
                      value={values.hospitalId}
                      options={hospitals}
                      onChange={(e) => {
                        // handleChange(e)
                        setFieldValue("hospitalId", e.target.value);
                      }}
                      onBlur={handleBlur}
                      error={Boolean(touched.hospitalId && errors.hospitalId)}
                      helperText={touched.hospitalId && errors.hospitalId}
                      disabled={true}
                    />
                  </Grid>
                )}

                <Grid item md={6}>
                  <MgpDD
                    label="Gender"
                    variant="outlined"
                    size="small"
                    name="gender"
                    multiselect={false}
                    fullWidth
                    value={values.gender}
                    options={genders}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(touched.gender && errors.gender)}
                    helperText={touched.gender && errors.gender}
                  />
                </Grid>
                <Grid item md={6}>
                  <MgpTextboxFormik
                    fullWidth
                    size="small"
                    label="Address"
                    name="address"
                    variant="outlined"
                    values={values}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    error={Boolean(touched.address && errors.address)}
                    helperText={touched.address && errors.address}
                  />
                </Grid>
                {!self &&
                (values.isSub ||
                  parseInt(values.roleId) ==
                    parseInt(ENV.secondary_investigator_role_id)) ? (
                  <Grid item md={6}>
                    <MgpDD
                      label={
                        Number(values.roleId) ===
                        Number(ENV.medical_lead_role_id)
                          ? "Select Principal Investigator"
                          : "Select Principal Investigator/Medical Lead"
                      }
                      variant="outlined"
                      size="small"
                      name="primaryInvestigatorDoctorIds"
                      multiselect={true}
                      fullWidth
                      value={values.primaryInvestigatorDoctorIds.map(String)}
                      options={doctors_and_medical_leads.filter(doctor => doctor.value !== user_id)}
                      disabled={values.hospitalId === "" || self ? true : false}
                      onChange={(e) => {
                        //handleChange(e);
                        setFieldValue(
                          "primaryInvestigatorDoctorIds",
                          e.target.value
                        );
                      }}
                      onBlur={handleBlur}
                      error={Boolean(
                        touched.primaryInvestigatorDoctorIds &&
                          errors.primaryInvestigatorDoctorIds
                      )}
                      helperText={
                        touched.primaryInvestigatorDoctorIds &&
                        errors.primaryInvestigatorDoctorIds
                      }
                    />
                  </Grid>
                ) : (
                  <Grid item md={6}></Grid>
                )}

                {!self ? (
                  <Grid item md={6}>
                    <FormControlLabel
                      sx={{
                        // color: colors.SECONDARY_TEXT,
                        fontSize: "12px",
                        "& .MuiFormControlLabel-label": {
                          fontSize: "12px", // Ensure this targets the label specifically
                        },
                      }}
                      control={
                        <MgpCheckbox
                          checked={values["isActive"]}
                          name="isActive"
                          label={"Activate"}
                          onChange={(event) => {
                            const { name, checked } = event.target;
                            // Update the values object with the new checkbox state
                            console.log(
                              "The e target in Values All is",
                              values
                            );
                            setFieldValue("isActive", checked);
                          }}
                          style={{
                            color: colors.SECONDARY_TEXT,
                          }}
                          size="small"
                          inputProps={{ "aria-label": "controlled" }}
                          disabled={self ? true : false}
                        />
                      }
                      label={"Activate"}
                    />
                  </Grid>
                ) : (
                  <></>
                )}

                {!self && (values.roleId == ENV.doctor_role_id || values.roleId == ENV.medical_lead_role_id) ? (
                  <Grid item md={6}>
                    <FormControlLabel
                      sx={{
                        // color: colors.SECONDARY_TEXT,
                        fontSize: "12px",
                        "& .MuiFormControlLabel-label": {
                          fontSize: "12px", // Ensure this targets the label specifically
                        },
                      }}
                      control={
                        <MgpCheckbox
                          checked={values["isSub"]}
                          name="isSub"
                          label={"Assign Sub Investigator Role"}
                          onChange={(event) => {
                            const { name, checked } = event.target;
                            // Update the values object with the new checkbox state
                            setFieldValue("isSub", checked);
                            setFieldValue("primaryInvestigatorDoctorIds", []);
                          }}
                          style={{
                            color: colors.SECONDARY_TEXT,
                          }}
                          size="small"
                          inputProps={{ "aria-label": "controlled" }}
                          disabled={self ? true : false}
                        />
                      }
                      label={"Assign Sub Investigator Role"}
                    />
                  </Grid>
                ) : (
                  <Grid item md={6}></Grid>
                )}

                <Grid item md={12}>
                  <Stack
                    spacing={2}
                    justifyContent={"center"}
                    direction={"row"}
                  >
                    <MgpButton onClick={closed} variant="contained">
                      Cancel
                    </MgpButton>
                    <MgpButton type="submit" variant="contained">
                      {user_id === "" ? "Save" : "Update"}
                    </MgpButton>
                  </Stack>
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>
      </Container>
      {loadingSubmit && (
        <Backdrop open={loadingSubmit} style={{ zIndex: 1 }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
    </>
  );
};
